import { Box } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import {
  GrowthProfilesTrowser,
  GrowthProfilesTrowserScope,
  growthProfilesTrowserTarget,
} from '@/modules/growthProfiles/GrowthProfilesTrowser/GrowthProfilesTrowser';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';

/** Help-tooltip copy keyed by the noun shown for the recipient row. */
export const GROW_BY_INFLATION_HELP_BODY = {
  disposition:
    'Inflation rate set in the growth profile will be applied to this disposition. Only dispositions set to a specific dollar amount will grow by inflation.',
  distribution:
    'Inflation rate set in the growth profile will be applied to this distribution. Only distributions set to a specific dollar amount will grow by inflation.',
} as const;

interface GrowByInflationPopperContentProps {
  /**
   * The growth profiles trowser target this row owns. The popper only opens the
   * trowser — the row below it holds the mount, because the popper unmounts as
   * soon as it loses the pointer.
   */
  trowserTarget: string;
  body?: string;
}

export function GrowByInflationPopperContent({
  trowserTarget,
  body = GROW_BY_INFLATION_HELP_BODY.disposition,
}: GrowByInflationPopperContentProps) {
  const growthProfilesTrowser = useTrowser(TrowserKey.GrowthProfiles);

  return (
    <PopperContent
      body={body}
      actions={
        <Button
          variant="secondary"
          size="xs"
          onClick={() => growthProfilesTrowser.open({ target: trowserTarget })}
        >
          Manage growth profiles
        </Button>
      }
      actionsSx={{
        marginBottom: 0,
        marginRight: 0,
        justifyContent: 'flex-start',
      }}
    />
  );
}

interface GrowByInflationCheckboxProps {
  /** Full field path to the growByInflation field */
  fieldName: string;
}

export function GrowByInflationCheckbox({
  fieldName,
}: GrowByInflationCheckboxProps) {
  const { householdId } = useHouseholdDetailsContext();
  const { control } = useFormContext();
  const growthProfilesTrowser = useTrowser(TrowserKey.GrowthProfiles);
  // One checkbox renders per recipient row, so the field path identifies this
  // row's trowser and a click opens only this row's copy.
  const trowserTarget = growthProfilesTrowserTarget(
    GrowthProfilesTrowserScope.GrowByInflationHelp,
    fieldName
  );

  return (
    <>
      <FormAwareCheckbox
        label={<Box sx={{ whiteSpace: 'nowrap' }}>Grow by inflation</Box>}
        fieldName={fieldName}
        control={control}
        contextualHelp={
          <GrowByInflationPopperContent trowserTarget={trowserTarget} />
        }
      />
      {growthProfilesTrowser.target === trowserTarget && householdId && (
        <GrowthProfilesTrowser
          onClose={growthProfilesTrowser.close}
          householdId={householdId}
        />
      )}
    </>
  );
}
